import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

const AUTH_URL = import.meta.env.VITE_AUTH_URL;
const STORAGE_KEY = "ginja:brand-settings";
const PORTAL_NAME = "Reinsurance";

export interface BrandColors {
  primary: string;
  secondary: string;
  accent: string;
}

export interface BrandSettings {
  brand_name: string;
  brand_tagline: string;
  logo_url: string;
  favicon_url: string;
  colors: BrandColors;
  default_timezone: string;
  default_currency: string;
  default_language: string;
  updated_at: string;
}

interface CachedBrand {
  brand: BrandSettings;
  etag: string | null;
}

const NEUTRAL_DEFAULTS: BrandSettings = {
  brand_name: "Ginja.AI",
  brand_tagline: "",
  logo_url: "/loginLogo.png",
  favicon_url: "/favicon.ico",
  colors: {
    primary: "#1E3A8A",
    secondary: "#0EA5E9",
    accent: "#F59E0B",
  },
  default_timezone: "Africa/Nairobi",
  default_currency: "KES",
  default_language: "en",
  updated_at: "",
};

function loadCache(): CachedBrand | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CachedBrand) : null;
  } catch {
    return null;
  }
}

function saveCache(payload: CachedBrand) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage unavailable — ignore
  }
}

async function fetchBrandSettings(): Promise<BrandSettings> {
  const cached = loadCache();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (cached?.etag) headers["If-None-Match"] = cached.etag;

  const res = await fetch(`${AUTH_URL}/brand-settings`, {
    method: "GET",
    headers,
  });

  if (res.status === 304 && cached) return cached.brand;
  if (res.status === 404) return cached?.brand ?? NEUTRAL_DEFAULTS;
  if (res.status === 401) return cached?.brand ?? NEUTRAL_DEFAULTS;
  if (!res.ok) {
    if (cached) return cached.brand;
    throw new Error(`Brand settings request failed: ${res.status}`);
  }

  const body = await res.json();
  const brand = body.data as BrandSettings;
  const etag = res.headers.get("ETag");
  saveCache({ brand, etag });
  return brand;
}

export function applyBrandTheme(brand: BrandSettings) {
  const root = document.documentElement.style;
  root.setProperty("--color-primary", brand.colors.primary);
  root.setProperty("--color-secondary", brand.colors.secondary);
  root.setProperty("--color-accent", brand.colors.accent);

  document.title = brand.brand_name
    ? `${brand.brand_name} — ${PORTAL_NAME}`
    : PORTAL_NAME;

  if (brand.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = brand.favicon_url;
  }
}

export function useBrandSettings() {
  const cached = loadCache();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["brand-settings"],
    queryFn: fetchBrandSettings,
    initialData: cached?.brand,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const brand = data ?? NEUTRAL_DEFAULTS;

  useEffect(() => {
    applyBrandTheme(brand);
  }, [brand]);

  return { brand, isLoading, isError };
}
